import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import { listCreatives, reviewCreative, type CreativeRow } from '@/lib/api';

/** API status values — identifiers, not labels. Never translate these; only how they are shown. */
const TABS = ['Pending', 'Approved', 'Rejected'] as const;

const CreativesPage: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Pending');
  const [rows, setRows] = useState<CreativeRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = (status: string) => { setRows(null); listCreatives(status).then(setRows).catch(() => setError(t('creatives.loadError', "Could not load creatives."))); };
  useEffect(() => { load(tab); }, [tab]);

  const review = async (c: CreativeRow, approve: boolean) => {
    const note = approve ? undefined : (window.prompt(t('creatives.rejectReason', 'Rejection reason (optional):')) ?? undefined);
    setBusy(c.id);
    try { await reviewCreative(c.id, approve, note); load(tab); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-body">{t('creatives.title', "Ad creatives")}</h1>
      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex gap-1">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-brand-50 text-brand-ink-strong' : 'text-muted hover:bg-canvas'}`}>
            {t}
          </button>
        ))}
      </div>

      {!rows ? (
        <div className="flex justify-center p-10"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <Card><p className="py-6 text-center text-sm text-subtle-ink">{t('creatives.emptyForTab', 'Nothing here.')}</p></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {rows.map((c) => (
            <Card key={c.id}>
              <img src={c.imageUrl} alt={c.title} className="mb-3 max-h-40 w-full rounded-lg object-contain" />
              <div className="font-semibold text-body">{c.title}</div>
              <div className="text-xs text-muted">{c.supplierName} · {c.mediaType}</div>
              <a href={c.targetUrl} target="_blank" rel="noopener noreferrer" className="mt-1 block truncate text-xs text-brand-ink hover:underline">{c.targetUrl}</a>
              {c.reviewNote && <div className="mt-1 text-xs text-subtle-ink">{t('creatives.note', 'Note')}: {c.reviewNote}</div>}
              {tab === 'Pending' && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" loading={busy === c.id} onClick={() => review(c, true)}>{t('creatives.approve', "Approve")}</Button>
                  <Button size="sm" variant="ghost" onClick={() => review(c, false)}>{t('creatives.reject', "Reject")}</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreativesPage;
